/**
 * RepairTotalsDialog · v2.7.65+E205
 * ═════════════════════════════════════════════════════════════════════
 * PIN-gated historical data repair for the "Final Total shows $0.00"
 * damage. Two-step by design: Preview is read-only, Apply writes and
 * drops an audit entry on every corrected trip.
 */
import { useState } from "react";
import adminApi from "@/lib/adminApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Wrench, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export const RepairTotalsDialog = ({ open, onClose, onApplied }) => {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [done, setDone] = useState(null);

  const run = async (apply) => {
    if (!pin) return toast.error("Supervisor PIN required");
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/trips/repair-totals", { pin, apply });
      setReport(data);
      if (apply) {
        setDone(data.result);
        toast.success(
          `Repaired ${data.result.totals_repaired_trips} trip total${data.result.totals_repaired_trips === 1 ? "" : "s"} · ` +
          `${data.result.statuses_normalized} ride${data.result.statuses_normalized === 1 ? "" : "s"} made visible again`,
        );
        onApplied?.();
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) toast.error("❌ Wrong supervisor PIN");
      else toast.error(err?.response?.data?.detail || err?.message || "Repair failed");
    } finally { setBusy(false); }
  };

  const nothingToDo = report && !report.total_repairs_count && !report.status_fixes_count;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="repair-totals-dialog">
        <DialogHeader>
          <DialogTitle className="text-amber-300 flex items-center gap-2">
            <Wrench className="h-5 w-5"/>Repair Trip Totals
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Card className="bg-zinc-900 border-zinc-800 p-4 text-xs text-zinc-400 space-y-2">
            <p>
              Scans every trip that is stored in more than one place and heals the copies that were
              left at <span className="text-amber-300 font-bold">$0.00</span> by the old completion
              mirror. Also restores completed rides that disappeared from this ledger because an old
              edit stamped a dispatch-board status onto them.
            </p>
            <p className="text-zinc-500">
              Preview is read-only. Nothing is written until you press Apply, and every corrected trip
              gets an audit-trail entry. Trips whose copies hold two different non-zero amounts are
              never touched — they&apos;re listed so you can settle them by hand.
            </p>
          </Card>

          <div className="bg-amber-900/20 border border-amber-700/40 rounded p-3">
            <label className="text-[10px] uppercase tracking-wider text-amber-300 font-bold flex items-center gap-1">
              <ShieldCheck className="h-3 w-3"/>Supervisor PIN
            </label>
            <Input
              type="password" value={pin} onChange={(e) => setPin(e.target.value)}
              placeholder="••••••" data-testid="repair-totals-pin"
              className="bg-zinc-950 border-zinc-700 mt-1"
            />
          </div>

          {report && (
            <Card className="bg-zinc-900 border-zinc-800 p-4 space-y-3" data-testid="repair-totals-report">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                <Stat label="Trips scanned" value={report.scanned_trips} testid="repair-stat-scanned"/>
                <Stat label="Totals to fix" value={report.total_repairs_count} accent="amber" testid="repair-stat-totals"/>
                <Stat label="Hidden rides" value={report.status_fixes_count} accent="amber" testid="repair-stat-status"/>
                <Stat label="Conflicts" value={report.conflicts_count} accent="orange" testid="repair-stat-conflicts"/>
              </div>

              {report.total_repairs_sample?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Money to restore</div>
                  <div className="space-y-1 max-h-40 overflow-y-auto font-mono text-[11px]">
                    {report.total_repairs_sample.map((r, i) => (
                      <div key={`${r.trip_id}-${r.collection}-${i}`} className="flex items-center justify-between gap-2 bg-zinc-950 border border-zinc-800 rounded px-2 py-1">
                        <span className="text-zinc-400 truncate">{r.trip_id}</span>
                        <Badge variant="outline" className="border-zinc-700 text-[9px]">{r.collection}</Badge>
                        <span className="text-amber-300">${Number(r.from).toFixed(2)} → ${Number(r.to).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {report.status_fixes_sample?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Rides to make visible again</div>
                  <div className="space-y-1 max-h-40 overflow-y-auto font-mono text-[11px]">
                    {report.status_fixes_sample.map((r, i) => (
                      <div key={`${r.trip_id}-${i}`} className="flex items-center justify-between gap-2 bg-zinc-950 border border-zinc-800 rounded px-2 py-1">
                        <span className="text-zinc-400 truncate">{r.trip_id}</span>
                        <span className="text-emerald-300">{r.from} → {r.to}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {report.conflicts_count > 0 && (
                <div className="text-[11px] text-orange-300">
                  {report.conflicts_count} trip{report.conflicts_count === 1 ? "" : "s"} hold two different
                  non-zero amounts and were skipped — open those trips and set the correct Final Total by hand.
                </div>
              )}

              {nothingToDo && (
                <div className="text-emerald-300 text-xs flex items-center gap-1.5" data-testid="repair-nothing-to-do">
                  <CheckCircle2 className="h-4 w-4"/>Everything is already in sync — nothing to repair.
                </div>
              )}
            </Card>
          )}

          {done && (
            <Card className="bg-emerald-950/20 border-emerald-700/40 p-3 text-xs text-emerald-200" data-testid="repair-totals-done">
              Applied · {done.totals_repaired_trips} trip total{done.totals_repaired_trips === 1 ? "" : "s"} restored
              ({done.totals_repaired_rows} record{done.totals_repaired_rows === 1 ? "" : "s"}) ·
              {" "}{done.statuses_normalized} ride{done.statuses_normalized === 1 ? "" : "s"} returned to the ledger.
            </Card>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="repair-totals-close">Close</Button>
          <Button
            variant="outline" onClick={() => run(false)} disabled={busy || !pin}
            data-testid="repair-totals-preview"
            className="border-amber-700/40 text-amber-300 hover:bg-amber-900/20"
          >
            {busy && !done ? <><Loader2 className="h-4 w-4 mr-1 animate-spin"/>Scanning…</> : "Preview"}
          </Button>
          <Button
            onClick={() => {
              if (!window.confirm(
                `Restore ${report.total_repairs_count} trip total${report.total_repairs_count === 1 ? "" : "s"} and ` +
                `${report.status_fixes_count} hidden ride${report.status_fixes_count === 1 ? "" : "s"}?\n\n` +
                `Every change is written to the trip audit trail.`,
              )) return;
              run(true);
            }}
            disabled={busy || !report || nothingToDo || !!done}
            data-testid="repair-totals-apply"
            className="bg-amber-400 hover:bg-amber-300 text-zinc-900 font-bold"
          >
            {busy && report ? <><Loader2 className="h-4 w-4 mr-1 animate-spin"/>Repairing…</> : "Apply Repair"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ label, value, accent, testid }) => (
  <div className="bg-zinc-950 border border-zinc-800 rounded p-2">
    <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
    <div
      className={`text-lg font-black tabular-nums ${
        accent === "amber" ? "text-amber-300" : accent === "orange" ? "text-orange-300" : "text-zinc-200"
      }`}
      data-testid={testid}
    >
      {value ?? 0}
    </div>
  </div>
);

export default RepairTotalsDialog;
